
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantsService } from '@/services';
import { useAuthStore } from '@/store/auth.store';
import type { CreateTenantDTO, UpdateTenantDTO } from '@/types';

const QUERY_KEY = 'tenants';

/**
 * Hook para listar tenants
 */
export function useTenants() {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: tenantsService.list,
  });
}

/**
 * Hook para obter um tenant específico
 */
export function useTenant(id: number) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => tenantsService.get(id),
    enabled: !!id,
  });
}

/**
 * Hook para criar tenant
 */
export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTenantDTO) => tenantsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook para atualizar tenant
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTenantDTO }) => tenantsService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

/**
 * Hook para deletar tenant
 */
export function useDeleteTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => tenantsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook para selecionar tenant ativo
 */
export function useSelectTenant() {
  const setTenantId = useAuthStore((state) => state.setTenantId);
  const queryClient = useQueryClient();

  return (tenantId: number | null) => {
    setTenantId(tenantId);
    // Invalidar todas as queries para recarregar com novo tenant
    queryClient.invalidateQueries();
  };
}
